/**
 * "My camera isn't listed" for the device-config pickers (ENG-4832 C1/C2,
 * ENG-4924 C2 from a "Not yet supported" pick).
 *
 * The add wizard owns its flow in the add store (`NotListedFlowConnected`);
 * every other caller of `<Brand>`/`<Model>` gets this local mount of the same
 * store-agnostic `<NotListedFlow>`, replacing the old "Enter Make & Model" /
 * Skip prompt. Suggestions come only from the collections the picker was
 * handed, so mounting it never adds registry requests.
 */
import type {
  IdentifiedBy,
  NotListedOpenedFrom
} from '#~/analytics/domains/device-identification.js';
import type { Brand as M_Brand, Model as M_Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { Collection } from '#~/api/device/registry/index.js';
// Direct module imports, not the identification barrel: the barrel also
// exports the add-store-connected mounts, whose store/registry graph the
// device-config pickers must not drag in.
// eslint-disable-next-line import/no-internal-modules
import { NotListedFlow } from '#~/video/components/add/identification/not-listed-flow.jsx';
// eslint-disable-next-line import/no-internal-modules
import type { NotListedQuery } from '#~/video/components/add/identification/suggestions.js';
import {
  buildNotListedSuggestions,
  toCatalogueRows
  // eslint-disable-next-line import/no-internal-modules
} from '#~/video/components/add/identification/suggestions.js';
// eslint-disable-next-line import/no-internal-modules
import type { NotListedStep } from '#~/video/components/add/store/types.js';
import type { ModelsByBrand } from './search.js';
import type { PickerIdentification } from './types.js';

/** How a picker opens the flow. */
export interface NotListedOpenOptions {
  /** Prefill for C1 (and the C2 title). */
  initial: NotListedQuery;

  /** Screen to open on; C1 unless a tagged row was picked (ENG-4924). Defaults to `'c1'`. */
  step?: NotListedStep | undefined;

  /** Entry point, reported as `opened_from` (ENG-4832). Defaults to `'list'`. */
  openedFrom?: NotListedOpenedFrom | undefined;
}

/** Inputs of `useNotListedFlow`. */
export interface UseNotListedFlowOptions {
  /** Brands the typed text is matched against. */
  brands: T_Maybe<Collection<M_Brand>>;

  /** Models per brand the typed text is matched against. */
  models: T_Maybe<ModelsByBrand>;

  /**
   * C2 Continue (or C1 Continue with nothing typed): commit the generic entry
   * and persist what was typed. `identifiedBy` is `not_listed` for the list's
   * action and `not_yet_supported_picked` for a tagged-row pick (ENG-4924);
   * typed fields are absent when nothing was typed.
   */
  onContinue: (identification: PickerIdentification) => void;

  /** "Did you mean …?" picked a listed model. */
  onDidYouMean: (pick: { brandId: M_Brand['id']; modelId: M_Model['id'] }) => void;

  /** Back from C1: nothing was chosen (e.g. restore the input). */
  onBack?: () => void;
}

/**
 * The `identifiedBy` the flow's Continue records, by entry point (ENG-4832 /
 * ENG-4924): same rule as the add store's `notListedIdentifiedBy`, kept local
 * so the device-config pickers do not import the add store.
 */
const toIdentifiedBy = (openedFrom: NotListedOpenedFrom): IdentifiedBy =>
  openedFrom === 'list' ? 'not_listed' : 'not_yet_supported_picked';

/**
 * Local open state + `<NotListedFlow>` element for a picker.
 *
 * @returns `open({ initial, step?, openedFrom? })` to show the flow, and the element to render.
 */
export const useNotListedFlow = ({
  brands,
  models,
  onBack,
  onContinue,
  onDidYouMean
}: UseNotListedFlowOptions): {
  element: ReactElement | null;
  open: (options: NotListedOpenOptions) => void;
} => {
  const [opened, setOpened] = useState<T_Maybe<NotListedOpenOptions>>();

  const rows = useMemo(() => toCatalogueRows(brands, models ?? {}), [brands, models]);
  const suggestions = useCallback(
    (query: NotListedQuery) => buildNotListedSuggestions(query, rows),
    [rows]
  );

  const openedFrom = opened?.openedFrom ?? 'list';

  const element = (
    <NotListedFlow
      initial={opened?.initial}
      onBack={() => {
        setOpened(undefined);
        onBack?.();
      }}
      onContinue={(typed) => {
        setOpened(undefined);
        // ENG-4832: the typed text is persisted with the generic driver
        // (StudioDevice typed_brand / typed_model / identified_by); only set
        // values are passed so an empty field clears any earlier text.
        onContinue({
          ...(typed.brand ? { typedBrand: typed.brand } : {}),
          ...(typed.model ? { typedModel: typed.model } : {}),
          identifiedBy: toIdentifiedBy(openedFrom)
        });
      }}
      onDidYouMean={(pick) => {
        setOpened(undefined);
        onDidYouMean(pick);
      }}
      open={opened !== undefined}
      openedFrom={openedFrom}
      step={opened?.step ?? 'c1'}
      suggestions={suggestions}
    />
  );

  return { element, open: setOpened };
};
